import { memo, startTransition, useCallback, type ComponentProps } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FileText, Plus, Search, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { DocumentCreator, type AutoSaveStatus } from "./DocumentCreator";
import { SectionErrorBoundary } from "@/components/SectionErrorBoundary";
import { useAttachmentPanelSources } from "./AttachmentPanel";
import { LibraryTree } from "./LibraryTree";
import { PinVerificationDialog } from "@/components/PinVerificationDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { MastermindSpace } from "@shared/schema";
import { RelationshipInspectorDrawer } from "@/components/RelationshipInspectorDrawer";
import type { DocumentActionsItem } from "@/components/DocumentActionsMenuContent";
import type { LibraryDocumentActions } from "@/components/LibraryDocumentRow";
import type { LibraryFolder } from "@/services/foldersService";

export interface RightSidebarAttachmentInnerProps {
  autoSaveStatus: AutoSaveStatus;
  activeTab: string;
  setActiveTab: (v: string) => void;
  editingItemId: string | null;
  handleActivateCreateTab: () => void;
  setAutoSaveStatus: (s: AutoSaveStatus) => void;
  setEditingItemId: (id: string | null) => void;
  setOpenDocumentHandler: (h: ((doc: unknown) => Promise<void>) | null) => void;
  docSearchQuery: string;
  setDocSearchQuery: (q: string) => void;
  folders: LibraryFolder[];
  kbItems: ComponentProps<typeof LibraryTree>["kbItems"];
  librarySpaces: MastermindSpace[];
  expandedFolderIds: Set<string>;
  selectedFolderId: string | null;
  toggleFolderExpanded: (id: string) => void;
  handleDocumentClick: (id: string, isLocked?: boolean) => void;
  handleFolderClick: (id: string) => void;
  handleRootClick: () => void;
  handleSpacesClick: () => void;
  handleSpaceClick: (id: string) => void;
  embeddingStatusMap: Record<string, boolean>;
  documentActions: LibraryDocumentActions;
  showPinDialogForDocument: boolean;
  setShowPinDialogForDocument: (o: boolean) => void;
  setPendingDocumentIdToOpen: (id: string | null) => void;
  pendingDocumentIdToOpen: string | null;
  setSelectedDocumentId: (id: string | null) => void;
  renameDialogOpen: boolean;
  setRenameDialogOpen: (o: boolean) => void;
  newTitle: string;
  setNewTitle: (t: string) => void;
  handleConfirmRename: () => void;
  renameIsPending: boolean;
  mergeDialogOpen: boolean;
  setMergeDialogOpen: (o: boolean) => void;
  mergeSourceItem: DocumentActionsItem | null;
  mergeTargetId: string;
  setMergeTargetId: (id: string) => void;
  noteItemsForMerge: { id: string; title?: string }[];
  handleConfirmMerge: () => void;
  mergeIsPending: boolean;
  moveToFolderDialogOpen: boolean;
  setMoveToFolderDialogOpen: (o: boolean) => void;
  moveFolderItem: DocumentActionsItem | null;
  moveFolderTargetId: string | null;
  setMoveFolderTargetId: (id: string | null) => void;
  handleConfirmMoveToFolder: () => void;
  moveToFolderIsPending: boolean;
  inspectorDrawerItem: {
    id: string;
    title: string;
    itemType: "note" | "reference";
    folderId: string | null;
    linkedSpaceIds: string[];
    linkedTopicIds: string[];
    linkedThreadIds: string[];
    tags: string[];
  } | null;
  inspectorOpen: boolean;
  setInspectorOpen: (o: boolean) => void;
  onInspectorRelationshipsUpdated: () => void;
}

export const RightSidebarAttachmentInner = memo(function RightSidebarAttachmentInner(
  props: RightSidebarAttachmentInnerProps
) {
  const sources = useAttachmentPanelSources();
  const queryClient = useQueryClient();

  const handleTabValueChange = useCallback(
    (value: string) => {
      if (value === "documents") {
        startTransition(() => {
          props.setActiveTab(value);
        });
      } else {
        props.setActiveTab(value);
      }
    },
    [props.setActiveTab]
  );

  return (
    <div className="flex flex-col h-full min-h-0 bg-sidebar">
      <div className="px-3 py-2 border-b flex items-center justify-between gap-2">
        <h2 className="text-xs font-medium text-muted-foreground">Documents</h2>
        <div className="flex items-center gap-1">
          {props.autoSaveStatus === "saving" && (
            <Badge variant="outline" className="text-xs animate-pulse">
              Saving...
            </Badge>
          )}
          {props.autoSaveStatus === "saved" && (
            <Badge variant="outline" className="text-xs text-green-600 dark:text-green-400">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Saved
            </Badge>
          )}
        </div>
      </div>

      <Tabs value={props.activeTab} onValueChange={handleTabValueChange} className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <TabsList className="mx-3 mt-1 mb-2 shrink-0 flex items-center w-full gap-2">
          <div className="flex flex-1 min-w-0">
            <TabsTrigger value="create" className="flex-1 text-xs" data-testid="tab-create-document">
              <Plus className="h-3 w-3 mr-1" />
              {props.editingItemId ? "Editing" : "Create"}
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex-1 text-xs" data-testid="tab-list-documents">
              <FileText className="h-3 w-3 mr-1" />
              Documents
            </TabsTrigger>
          </div>
          <div
            id="doc-creator-tab-actions"
            className={props.activeTab === "create" ? "flex items-center gap-2 shrink-0 mr-[10px]" : "hidden"}
            aria-hidden={props.activeTab !== "create"}
          />
        </TabsList>

        <SectionErrorBoundary sectionName="Document creator">
          <DocumentCreator
            onActivateCreateTab={props.handleActivateCreateTab}
            onAutoSaveStatusChange={props.setAutoSaveStatus}
            onEditingItemIdChange={props.setEditingItemId}
            onOpenDocumentHandlerChange={props.setOpenDocumentHandler}
            tabBarActionsContainerId="doc-creator-tab-actions"
          />
        </SectionErrorBoundary>

        <TabsContent value="documents" className="flex-1 m-0 mt-0 flex flex-col min-h-0 overflow-hidden">
          <div className="shrink-0 p-3 border-b bg-background">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                placeholder="Search folders..."
                value={props.docSearchQuery}
                onChange={(e) => props.setDocSearchQuery(e.target.value)}
                className="pl-8 h-8 text-xs"
                data-testid="input-search-sidebar-docs"
              />
            </div>
          </div>

          <div className="flex-1 min-h-0 min-w-0 flex flex-col">
            <LibraryTree
              docSearchQuery={props.docSearchQuery}
              folders={props.folders}
              kbItems={props.kbItems}
              sources={sources}
              librarySpaces={props.librarySpaces}
              expandedFolderIds={props.expandedFolderIds}
              selectedFolderId={props.selectedFolderId}
              onToggleFolderExpanded={props.toggleFolderExpanded}
              onDocumentClick={props.handleDocumentClick}
              onFolderClick={props.handleFolderClick}
              onRootClick={props.handleRootClick}
              onSpacesClick={props.handleSpacesClick}
              onSpaceClick={props.handleSpaceClick}
              embeddingStatusMap={props.embeddingStatusMap}
              documentActions={props.documentActions}
            />
          </div>
        </TabsContent>
      </Tabs>

      <PinVerificationDialog
        open={props.showPinDialogForDocument}
        onOpenChange={(open) => {
          if (!open) props.setPendingDocumentIdToOpen(null);
          props.setShowPinDialogForDocument(open);
        }}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["pin-status"] });
          if (props.pendingDocumentIdToOpen) {
            props.setSelectedDocumentId(props.pendingDocumentIdToOpen);
            props.setActiveTab("create");
            props.setPendingDocumentIdToOpen(null);
            props.setShowPinDialogForDocument(false);
          }
        }}
        title="Unlock document"
        description="Enter your PIN to edit this document."
      />

      <Dialog open={props.renameDialogOpen} onOpenChange={props.setRenameDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="sidebar-new-title">New Title</Label>
              <Input
                id="sidebar-new-title"
                value={props.newTitle}
                onChange={(e) => props.setNewTitle(e.target.value)}
                placeholder="Enter new title..."
                onKeyDown={(e) => e.key === "Enter" && props.handleConfirmRename()}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => props.setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={props.handleConfirmRename} disabled={props.renameIsPending || !props.newTitle.trim()}>
              {props.renameIsPending ? "Renaming..." : "Rename"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={props.mergeDialogOpen} onOpenChange={props.setMergeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Merge Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Select a document to merge &quot;{props.mergeSourceItem?.title}&quot; into. The content will be appended to the target
              document, and this document will be deleted.
            </p>
            <div className="space-y-2">
              <Label htmlFor="merge-target">Target Document</Label>
              <Select value={props.mergeTargetId} onValueChange={props.setMergeTargetId}>
                <SelectTrigger id="merge-target">
                  <SelectValue placeholder="Select a document..." />
                </SelectTrigger>
                <SelectContent>
                  {props.noteItemsForMerge.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.title ?? "Untitled"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => props.setMergeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={props.handleConfirmMerge} disabled={props.mergeIsPending || !props.mergeTargetId}>
              {props.mergeIsPending ? "Merging..." : "Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={props.moveToFolderDialogOpen} onOpenChange={props.setMoveToFolderDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Move to Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">Move &quot;{props.moveFolderItem?.title}&quot; to a folder.</p>
            <div className="space-y-2">
              <Label>Folder</Label>
              <Select
                value={props.moveFolderTargetId ?? "none"}
                onValueChange={(v) => props.setMoveFolderTargetId(v === "none" ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a folder..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No folder (unfiled)</SelectItem>
                  {props.folders.map((f) => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => props.setMoveToFolderDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={props.handleConfirmMoveToFolder} disabled={props.moveToFolderIsPending}>
              {props.moveToFolderIsPending ? "Moving..." : "Move"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <RelationshipInspectorDrawer
        item={props.inspectorDrawerItem}
        open={props.inspectorOpen}
        onOpenChange={props.setInspectorOpen}
        onRelationshipsUpdated={props.onInspectorRelationshipsUpdated}
      />
    </div>
  );
});

RightSidebarAttachmentInner.displayName = "RightSidebarAttachmentInner";
